import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState, Fragment } from 'react';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import {
  Button,
  Chip,
  Dialog,
  LinearProgress,
  linearProgressClasses,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  styled,
  useMediaQuery,
  Switch
} from '@mui/material';

// third-party
import { useFilters, useExpanded, useGlobalFilter, useRowSelect, useSortBy, useTable, usePagination } from 'react-table';

// project-imports
import MainCard from 'components/MainCard';
import ScrollX from 'components/ScrollX';
import IconButton from 'components/@extended/IconButton';
import { PopupTransition } from 'components/@extended/Transitions';
import {
  CSVExport,
  HeaderSort,
  IndeterminateCheckbox,
  SortingSelect,
  TablePagination,
  TableRowSelection
} from 'components/third-party/ReactTable';

import AddCustomer from 'sections/apps/customer/AddCustomer';
import CustomerView from 'sections/apps/customer/CustomerView';
import AlertCustomerDelete from 'sections/apps/customer/AlertCustomerDelete';
import { renderFilterTypes, GlobalFilter } from 'utils/react-table';

// assets
import { Add, Edit, Eye, Trash } from 'iconsax-react';
import { ThemeMode } from 'config';
import useAuth from 'hooks/useAuth';
import AlertServiceDelete from './AlertServiceDelete';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import EditService from './EditService';
import Avatar from 'components/@extended/Avatar';

const avatarImage = require.context('assets/images/users', true);

// ==============================|| REACT TABLE ||============================== //

function ReactTable({ columns, data, renderRowSubComponent, handleAdd }) {
  const theme = useTheme();
  const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));

  const filterTypes = useMemo(() => renderFilterTypes, []);
  const sortBy = { id: 'id', desc: false };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    setHiddenColumns,
    allColumns,
    visibleColumns,
    rows,
    page,
    gotoPage,
    setPageSize,
    state: { globalFilter, selectedRowIds, pageIndex, pageSize, expanded },
    preGlobalFilteredRows,
    setGlobalFilter,
    setSortBy,
    selectedFlatRows
  } = useTable(
    {
      columns,
      data,
      filterTypes,
      initialState: { pageIndex: 0, pageSize: 10, hiddenColumns: ['avatar', 'email'], sortBy: [sortBy] }
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  useEffect(() => {
    if (matchDownSM) {
      setHiddenColumns(['age', 'contact', 'visits', 'email', 'status', 'avatar']);
    } else {
      setHiddenColumns(['avatar', 'email']);
    }
    // eslint-disable-next-line
  }, [matchDownSM]);

  return (
    <>
      <TableRowSelection selected={Object.keys(selectedRowIds).length} />
      <Stack spacing={3}>
        <Stack
          direction={matchDownSM ? 'column' : 'row'}
          spacing={1}
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 3, pb: 0 }}
        >
          <GlobalFilter preGlobalFilteredRows={preGlobalFilteredRows} globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
          <Stack direction={matchDownSM ? 'column' : 'row'} alignItems="center" spacing={2}>
            <SortingSelect sortBy={sortBy.id} setSortBy={setSortBy} allColumns={allColumns} />
            <Button variant="contained" startIcon={<Add />} onClick={handleAdd} size="small" className='bg-orange-500 hover:bg-orange-400'>
              Agregar Servicio
            </Button>
            <Stack orientation="col" className='items-center'>
              <Typography variant='body1' className='text-orange-500'> Descargar CSV</Typography>
              <CSVExport data={selectedFlatRows.length > 0 ? selectedFlatRows.map((d) => d.original) : data} filename={'service-list.csv'} />
            </Stack>
          </Stack>
        </Stack>
        <Table {...getTableProps()}>
          <TableHead>
            {headerGroups.map((headerGroup) => (
              <TableRow key={headerGroup} {...headerGroup.getHeaderGroupProps()} sx={{ '& > th:first-of-type': { width: '58px' } }}>
                {headerGroup.headers.map((column) => (
                  <TableCell key={column} {...column.getHeaderProps([{ className: column.className }])}>
                    <HeaderSort column={column} sort />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              const rowProps = row.getRowProps();

              return (
                <Fragment key={i}>
                  <TableRow
                    {...row.getRowProps()}
                    // onClick={() => {
                    //   row.toggleRowSelected();
                    // }}
                    sx={{ cursor: 'pointer', bgcolor: row.isSelected ? alpha(theme.palette.primary.lighter, 0.35) : 'inherit' }}
                  >
                    {row.cells.map((cell) => (
                      <TableCell key={cell} {...cell.getCellProps([{ className: cell.column.className }])}>
                        {cell.render('Cell')}
                      </TableCell>
                    ))}
                  </TableRow>
                  {row.isExpanded && renderRowSubComponent({ row, rowProps, visibleColumns, expanded })}
                </Fragment>
              );
            })}
            <TableRow sx={{ '&:hover': { bgcolor: 'transparent !important' } }}>
              <TableCell sx={{ p: 2, py: 3 }} colSpan={9}>
                <TablePagination gotoPage={gotoPage} rows={rows} setPageSize={setPageSize} pageSize={pageSize} pageIndex={pageIndex} />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Stack>
    </>
  );
}

ReactTable.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  getHeaderProps: PropTypes.func,
  handleAdd: PropTypes.func,
  renderRowSubComponent: PropTypes.any
};

// ==============================|| CUSTOMER - LIST ||============================== //

const ServiceListPage = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const [data, setData] = useState([]);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const { getServiceList, updatePetService } = useAuth();
  const [open, setOpen] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [customerDeleteId, setCustomerDeleteId] = useState('');
  const [add, setAdd] = useState(false);

  const handleAdd = () => {
    setAdd(!add);
  };

  const handleNewService = () => {
    setCustomer(null);
    setAdd(!add);
  };

  const handleClose = () => {
    setOpen(!open);
  };

  const fetchData = () => {
    getServiceList()
      .then((response) => {
        // console.log(response)
        setData(response.data.data)
      }).catch((error) => {
        console.error(error)
      }).finally(() => { });
  }

  useEffect(() => {
    if (data.length <= 0) {
      fetchData();
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: '#',
        accessor: 'id',
        className: 'cell-center'
      },
      {
        Header: 'Nombre',
        accessor: 'name',
        Cell: ({ row }) => {
          const { values, original } = row;
          return (
            <Stack direction="row" spacing={1.5} alignItems="center">
              {original.img ? (
                <Avatar alt={original.name} size="sm" src={original.img} />
              ) : (
                <Avatar alt={original.name} size="sm">{original.name.charAt(0).toUpperCase()}</Avatar>
              )}
              <Stack spacing={0}>
                <Typography variant="subtitle1">{values.name}</Typography>
              </Stack>
            </Stack>
          );
        }
      },
      {
        Header: 'Tiempo de asistencia',
        accessor: 'description_time',
        Cell: ({ row }) => {
          const { values } = row;
          return (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="body1">{values.description_time || 'No definido'}</Typography>
            </Stack>
          );
        }
      },
      {
        Header: 'Estado',
        accessor: 'status',
        Cell: ({ value }) => {
          switch (value) {
            case 'inactive':
              return <Chip label="Inactivo" color="warning" size="small" variant="light" />;
            case 'active':
              return <Chip label="Activo" color="success" size="small" variant="light" />;
            case 'pause':
            default:
              return <Chip color="info" label={value[0].toUpperCase() + value.slice(1).toLowerCase()} size="small" variant="light" />;
          }
        }
      },
      {
        Header: 'SUSPENDER',
        Cell: ({ row }) => {
          const { values } = row;
          return (
            <Switch defaultChecked={values.status === "inactive"} checked={values.status === "inactive"} onChange={(e) => {
              e.stopPropagation();
              // handleStatusDialogClose();
              // setCustomerDeleteId(values.id);
              // setCustomerDeleteName(values.name);
              handleClose();
              setCustomerDeleteId(row.original);
            }} className='z-50' />
          )
        }
      },
      {
        Header: 'Acciones',
        className: 'cell-center',
        disableSortBy: true,
        Cell: ({ row }) => {
          const collapseIcon = row.isExpanded ? <Add style={{ color: theme.palette.error.main, transform: 'rotate(45deg)' }} /> : <Eye />;
          return (
            <Stack direction="row" alignItems="center" justifyContent="start" spacing={0}>
              {/* <Tooltip
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: mode === ThemeMode.DARK ? theme.palette.grey[50] : theme.palette.grey[700],
                      opacity: 0.9
                    }
                  }
                }}
                title="View"
              >
                <IconButton
                  color="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    row.toggleRowExpanded();
                  }}
                >
                  {collapseIcon}
                </IconButton>
              </Tooltip> */}
              <Tooltip
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: mode === ThemeMode.DARK ? theme.palette.grey[50] : theme.palette.grey[700],
                      opacity: 0.9
                    }
                  }
                }}
                title="Editar"
              >
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCustomer(row.original);
                    handleAdd();
                  }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
              {/* <Tooltip
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: mode === ThemeMode.DARK ? theme.palette.grey[50] : theme.palette.grey[700],
                      opacity: 0.9
                    }
                  }
                }}
                title="Eliminar"
              >
                <IconButton
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                    setCustomerDeleteId(row.original);
                  }}
                >
                  <Trash />
                </IconButton>
              </Tooltip> */}
            </Stack>
          );
        }
      }
    ],
    [theme]
  );

  const handleSuspend = (service) => {
    try {
      setLoadingUpdate(true);
      const newStatus = service.status === 'active' ? 'inactive' : 'active';
      let formData = new FormData();
      formData.append("status", newStatus);

      updatePetService(service?.uuid, formData)
        .then((response) => {
          if (response.data.success) {
            setData((prevData) =>
              prevData.map((s) =>
                s.id === service.id ? { ...s, status: newStatus } : s
              )
            );

            dispatch(
              openSnackbar({
                open: true,
                message: 'Servicio actualizado correctamente.',
                variant: 'alert',
                alert: {
                  color: 'success'
                },
                close: true
              })
            );

            handleClose();
          } else {
            dispatch(
              openSnackbar({
                open: true,
                message: response.data.message,
                variant: 'alert',
                alert: {
                  color: 'error'
                },
                close: true
              })
            );
          }

        }).catch((error) => {
          console.error(error)
          dispatch(
            openSnackbar({
              open: true,
              message: "Ha ocurrido un error al actualizar el servicio.",
              variant: 'alert',
              alert: {
                color: 'error'
              },
              close: true
            })
          );
        }).finally(() => {
          setLoadingUpdate(false);
        });
    } catch (err) {
      console.error(err)
    }
  };

  const renderRowSubComponent = useCallback(({ row }) => <CustomerView data={data[Number(row.id)]} />, [data]);

  const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 10,
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
    },
    [`& .${theme.palette.primary}`]: {
      borderRadius: 5,
      backgroundColor: theme.palette.mode === 'light' ? '#1a90ff' : '#308fe8',
    },
  }));

  return (
    <MainCard content={false}>
      <ScrollX>
        <ReactTable columns={columns} data={data} handleAdd={handleNewService} renderRowSubComponent={renderRowSubComponent} />
        {data.length <= 0 && <BorderLinearProgress variant="indeterminate" value={50} />}
      </ScrollX>
      <AlertServiceDelete service={customerDeleteId} open={open} handleClose={handleClose} handleSuspend={handleSuspend} loading={loadingUpdate} />
      <Dialog
        maxWidth="sm"
        TransitionComponent={PopupTransition}
        keepMounted
        fullWidth
        onClose={handleAdd}
        open={add}
        sx={{ '& .MuiDialog-paper': { p: 0 }, transition: 'transform 225ms' }}
        aria-describedby="alert-dialog-slide-description"
      >
        <EditService customer={customer} onCancel={handleAdd} onUpdate={fetchData} />
      </Dialog>
    </MainCard>
  );
};

ServiceListPage.propTypes = {
  row: PropTypes.object,
  values: PropTypes.object,
  avatar: PropTypes.object,
  message: PropTypes.string,
  email: PropTypes.string,
  value: PropTypes.object,
  isExpanded: PropTypes.bool,
  toggleRowExpanded: PropTypes.func,
  getToggleAllPageRowsSelectedProps: PropTypes.func,
  id: PropTypes.number
};

export default ServiceListPage;
